import { useState } from "react";
import { KeyRound, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import Loading from "@/components/Loading";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  isPasskeySupported,
  enrollPasskey,
  enrollTotp,
  verifyTotp,
  type TotpEnrollment,
} from "../mfa";

const TOTP_CODE_LENGTH = 6;

const errorMessage = (e: unknown, fallback: string) =>
  e instanceof Error && e.message ? e.message : fallback;

/**
 * Dialog for adding a new second-factor device from settings. Reuses the same
 * enroll helpers as the first-time setup flow; the user is already
 * second-factor-verified here, so enrollment just adds another device.
 */
export const AddDeviceDialog = ({
  open,
  onOpenChange,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
}) => {
  const [method, setMethod] = useState<"totp" | "passkey" | null>(null);
  const [totp, setTotp] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setMethod(null);
    setTotp(null);
    setCode("");
    setBusy(false);
    setError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  };

  const succeed = () => {
    reset();
    onAdded();
  };

  const startTotp = async () => {
    setError(null);
    setBusy(true);
    try {
      const enrollment = await enrollTotp();
      setTotp(enrollment);
      setMethod("totp");
    } catch (e) {
      setError(errorMessage(e, "Could not start authenticator setup."));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (value: string) => {
    if (!totp || value.length !== TOTP_CODE_LENGTH) {
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await verifyTotp(totp.factorId, value);
      succeed();
    } catch (e) {
      setCode("");
      setError(errorMessage(e, "That code wasn't valid. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const startPasskey = async () => {
    setError(null);
    setMethod("passkey");
    setBusy(true);
    try {
      await enrollPasskey();
      succeed();
    } catch (e) {
      setMethod(null);
      setError(errorMessage(e, "Passkey setup was cancelled or failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {method === "totp"
              ? "Scan with your authenticator"
              : "Add a device"}
          </DialogTitle>
          <DialogDescription>
            {method === "totp"
              ? "Scan the QR code with an authenticator app, then enter the 6-digit code."
              : "Add another way to verify it's you when signing in."}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {method === "totp" && totp ? (
          <div className="space-y-5 py-2">
            <div className="flex justify-center">
              <img
                src={totp.qrCode}
                alt="Authenticator QR code"
                className="h-44 w-44"
              />
            </div>
            <div className="text-center text-sm text-muted-foreground">
              Can't scan? Enter this key manually:
              <div className="mt-1 font-mono text-foreground break-all">
                {totp.secret}
              </div>
            </div>
            <div className="flex justify-center">
              <InputOTP
                value={code}
                onChange={(value) => {
                  setCode(value);
                  if (value.length === TOTP_CODE_LENGTH) {
                    submitCode(value);
                  }
                }}
                disabled={busy}
                autoFocus
                maxLength={TOTP_CODE_LENGTH}
              >
                <InputOTPGroup>
                  {Array.from({ length: TOTP_CODE_LENGTH }).map((_, i) => (
                    <InputOTPSlot key={i} index={i} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            {busy && (
              <div className="flex justify-center">
                <Loading className="w-7 h-7" />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 py-2">
            {isPasskeySupported() && (
              <Button
                onClick={startPasskey}
                disabled={busy}
                className="w-full h-14 justify-start gap-3"
              >
                <KeyRound className="h-5 w-5" />
                <span className="flex flex-col items-start">
                  <span className="font-semibold">Add a passkey</span>
                  <span className="text-xs font-normal opacity-80">
                    Face ID, Touch ID, or a security key
                  </span>
                </span>
                {busy && method === "passkey" && (
                  <Loading className="w-5 h-5 ml-auto" />
                )}
              </Button>
            )}
            <Button
              onClick={startTotp}
              disabled={busy}
              variant="outline"
              className="w-full h-14 justify-start gap-3"
            >
              <Smartphone className="h-5 w-5" />
              <span className="flex flex-col items-start">
                <span className="font-semibold">Add an authenticator app</span>
                <span className="text-xs font-normal opacity-80">
                  Get a code from an app like Google Authenticator
                </span>
              </span>
              {busy && method !== "passkey" && (
                <Loading className="w-5 h-5 ml-auto" />
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
